import ky from "ky"
import type { KyInstance, Options as _KyOptions } from "ky"

/**
 * Gets the default base URL from the configuration
 * @param suffix The suffix to append to the base URL, should not include a leading slash, defaults to `v1`
 * @returns The default base URL
 */
export function getDefaultBaseUrl(suffix: string = "v1"): string {
  const config = useConfig()
  return config.NUXT_PUBLIC_CORE_SERVER_ORIGIN + suffix || ""
}

/** Creates a basic Ky instance with the given configuration */
export function createBaseKyInstance(config?: ApiServiceConfig): KyInstance {
  const { baseUrl, onUnauthorized, ...delegatedConfig } = config || {}
  const prefixUrl = baseUrl || getDefaultBaseUrl()

  return ky.create({
    prefixUrl,
    retry: {
      limit: 0,
    },
    ...delegatedConfig,
  })
}

/** Creates a Ky instance with authentication and other common settings */
export function createAuthenticatedKyInstance(config?: ApiServiceConfig): KyInstance {
  const isRefreshing: Ref<boolean> = ref(false)
  const baseInstance = createBaseKyInstance(config)

  const instance = baseInstance.extend({
    credentials: "include",

    hooks: {
      beforeRequest: [
        (request) => {
          if (isServer) return

          const pageViewId = usePageViewId()
          if (!request.headers?.get("X-Page-View-Id") && pageViewId.value) {
            request.headers.set("X-Page-View-Id", pageViewId.value)
          }

          const { $i18n } = useNuxtApp()

          if (!request.headers?.get("Accept-Language") && $i18n.locale.value) {
            request.headers.set("Accept-Language", $i18n.locale.value)
          }

          const { userExists, user } = useUser()

          if (!userExists.value) return

          if (!request.headers?.get("Authorization") && user.value?.accessToken) {
            request.headers.set("Authorization", `Bearer ${user.value.accessToken}`)
          }
        },
      ],

      afterResponse: [
        // retry with a fresh token on a 403 error
        async (request, options, response) => {
          const { userExists, user, isAuthenticated } = useUser()

          if (!userExists.value) return

          if (
            /* Quit if user is not logged in */
            !isAuthenticated.value ||
            /* Quit if error is not equal to 401 */
            response?.status !== 401 ||
            /* Quit if current signal is aborted */
            request.signal.aborted ||
            /* Quit if currently refreshing */
            isRefreshing.value
          ) {
            return
          }

          if (config?.onUnauthorized?.(request)) {
            return
          }

          try {
            const { refreshToken } = user.value!

            if (!refreshToken) throw new Error("No refresh token found")

            // Get a fresh token
            isRefreshing.value = true
            const { accessToken } = await postRefreshToken(refreshToken)

            // Retry with the token
            request.headers.set("Authorization", `Bearer ${accessToken}`)

            return ky(request)
          } catch (err) {
            console.error("error while refreshing token", err)
            await logoutUser()
          } finally {
            isRefreshing.value = false
          }
        },
      ],
    },
  })

  return instance
}

/** Sends a POST request to refresh the user's access token using their refresh token. */
async function postRefreshToken(
  refreshToken: string
): Promise<Pick<User, "accessToken" | "refreshToken" | "expiresIn">> {
  try {
    // Using a non-instance approach to avoid circular dependencies
    const kyInstance = createBaseKyInstance()
    const res = await kyInstance
      .post("/auth/refreshtoken", { json: { refreshToken }, credentials: "include" })
      .json()

    const data = (res as any).data.data

    // Update the user with new tokens
    const { mergeUser } = useUser()
    mergeUser(data as User)

    return data
  } catch (err) {
    return logoutUser() as any
  }
}

/** Logs out the user due to api token errors */
async function logoutUser(): Promise<void> {
  const { $i18n } = useNuxtApp()
  const popup = usePopup()

  popup.open({ type: "warning", title: $i18n.t("system.logout_due_inactivity") })
  return afterLogoutActions()
}
